/* ==========================================================================
   Zapatos Obi — header.js
   Menú móvil, overlay de búsqueda, cabecera fija, rotación de anuncios,
   mega menú (aria-expanded, Escape) y acordeón del pie.
   Ganchos: [data-mobile-menu] #MobileMenu, [data-menu-toggle]/[data-menu-close],
   [data-search-overlay] #SearchOverlay, [data-search-toggle]/[data-search-close],
   [data-announcement][data-autorotate][data-speed] .announcement__item,
   .has-dropdown > [data-mega-trigger] (modo columnas),
   [data-mega-tabs]/[data-mega-tab]/[data-mega-panel] (modo auto),
   [data-footer-disclosure]. Publica --obi-sticky-offset y .is-scrolled.
   ========================================================================== */
(function () {
  'use strict';

  function debounce(fn, wait) {
    var t;
    return function () {
      var ctx = this;
      var args = arguments;
      clearTimeout(t);
      t = setTimeout(function () {
        fn.apply(ctx, args);
      }, wait || 10);
    };
  }

  function trapInitialFocus(container) {
    var focusable = container.querySelector('a, button, input, [tabindex]');
    if (focusable) focusable.focus();
  }

  class HeaderController extends HTMLElement {
    connectedCallback() {
      this.mobileMenu = this.querySelector('[data-mobile-menu]');
      this.searchOverlay = this.querySelector('[data-search-overlay]');
      this.searchToggle = this.querySelector('[data-search-toggle]');
      this.menuToggle = this.querySelector('[data-menu-toggle]');

      this._bindEvents();
      this._initMega();
      this._initSticky();
      this._initAnnouncement();
    }

    _bindEvents() {
      var self = this;

      this.querySelectorAll('[data-menu-toggle]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          self.toggleMenu(true);
        });
      });
      this.querySelectorAll('[data-menu-close]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          self.toggleMenu(false);
        });
      });

      this.querySelectorAll('[data-search-toggle]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          self.toggleSearch();
        });
      });
      this.querySelectorAll('[data-search-close]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          self.toggleSearch(false);
        });
      });

      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') {
          self.toggleMenu(false);
          self.toggleSearch(false);
          self.closeMega();
        }
      });

      /* mega-menú con pestañas (modo auto): la familia bajo el cursor muestra
         su panel. En la cuadrícula de letras (MARCAS) el hover cambiaría de
         letra sin querer al cruzar el ratón: ahí solo se activa con clic. */
      this.querySelectorAll('[data-mega-tabs]').forEach(function (wrap) {
        var tabs = wrap.querySelectorAll('[data-mega-tab]');
        var panels = wrap.querySelectorAll('[data-mega-panel]');
        var clickOnly = !!wrap.querySelector('.header__mega-tabs--letters');
        tabs.forEach(function (tab) {
          var activate = function () {
            var idx = tab.getAttribute('data-mega-tab');
            tabs.forEach(function (t) {
              t.classList.toggle('is-active', t === tab);
            });
            panels.forEach(function (p) {
              p.classList.toggle('is-active', p.getAttribute('data-mega-panel') === idx);
            });
          };
          if (!clickOnly) tab.addEventListener('mouseenter', activate);
          tab.addEventListener('focus', activate);
          tab.addEventListener('click', function (e) {
            /* las pestañas sin destino (letras de MARCAS) solo activan su panel */
            if (tab.getAttribute('href') === '#') {
              e.preventDefault();
              activate();
            }
          });
        });
      });
    }

    /* Mega menú de escritorio: el panel se abre por CSS (:hover / :focus-within);
       aquí solo se sincroniza aria-expanded del enlace de primer nivel. */
    _initMega() {
      this.querySelectorAll('.has-dropdown').forEach(function (item) {
        var trigger = item.querySelector('[data-mega-trigger]');
        if (!trigger) return;
        var set = function (open) {
          trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
        };
        item.addEventListener('mouseenter', function () {
          set(true);
        });
        item.addEventListener('mouseleave', function () {
          set(false);
        });
        item.addEventListener('focusin', function () {
          set(true);
        });
        item.addEventListener('focusout', function (e) {
          if (!item.contains(e.relatedTarget)) set(false);
        });
      });
    }

    /* Escape: si el foco está dentro de un panel abierto, sacarlo (lo cierra) */
    closeMega() {
      var active = document.activeElement;
      var item = active && active.closest ? active.closest('.has-dropdown') : null;
      if (!item) return;
      var trigger = item.querySelector('[data-mega-trigger]');
      if (trigger && trigger !== active) trigger.focus();
      else if (active.blur) active.blur();
    }

    toggleMenu(open) {
      if (!this.mobileMenu) return;
      this.mobileMenu.hidden = !open;
      document.documentElement.classList.toggle('overflow-hidden', open);
      if (this.menuToggle) this.menuToggle.setAttribute('aria-expanded', String(open));
      if (open) trapInitialFocus(this.mobileMenu);
      else if (this.menuToggle) this.menuToggle.focus();
    }

    toggleSearch(force) {
      if (!this.searchOverlay) return;
      var root = document.documentElement;
      var isOpen = root.classList.contains('search-drawer-open');
      var open = typeof force === 'boolean' ? force : !isOpen;
      if (open === isOpen) return;
      root.classList.toggle('search-drawer-open', open);
      if (this.searchToggle) this.searchToggle.setAttribute('aria-expanded', String(open));
      if (open) {
        var input = this.searchOverlay.querySelector('input[type="search"]');
        if (input) input.focus();
      } else if (this.searchToggle) {
        this.searchToggle.focus();
      }
    }

    _initSticky() {
      var self = this;

      /* Altura de la cabecera fija como variable CSS: otros elementos
         sticky (p. ej. la barra de filtros) se pegan justo debajo. */
      var setOffset = function () {
        var sticky = self.classList.contains('header-wrapper--sticky');
        document.documentElement.style.setProperty(
          '--obi-sticky-offset',
          sticky ? self.offsetHeight + 'px' : '0px'
        );
      };
      setOffset();
      window.addEventListener('resize', debounce(setOffset, 100));

      if (!this.classList.contains('header-wrapper--sticky')) return;
      window.addEventListener(
        'scroll',
        debounce(function () {
          self.classList.toggle('is-scrolled', window.scrollY > 40);
        }, 10),
        { passive: true }
      );
    }

    _initAnnouncement() {
      var bar = document.querySelector('[data-announcement]');
      if (!bar || bar.dataset.autorotate !== 'true') return;
      var items = Array.prototype.slice.call(bar.querySelectorAll('.announcement__item'));
      if (items.length < 2) return;
      var index = 0;
      var speed = (parseInt(bar.dataset.speed, 10) || 5) * 1000;
      setInterval(function () {
        items[index].classList.remove('is-active');
        index = (index + 1) % items.length;
        items[index].classList.add('is-active');
      }, speed);
    }
  }

  if (!customElements.get('header-controller')) {
    customElements.define('header-controller', HeaderController);
  }

  /* Columnas del footer: acordeón en móvil, siempre abiertas en PC.
     Se aplica solo al cruzar el breakpoint (no en cada resize de scroll). */
  function initFooterDisclosure() {
    var items = document.querySelectorAll('[data-footer-disclosure]');
    if (!items.length) return;
    var mq = window.matchMedia('(max-width: 749px)');
    var apply = function () {
      items.forEach(function (d) {
        d.open = !mq.matches;
      });
    };
    apply();
    if (mq.addEventListener) mq.addEventListener('change', apply);
    else if (mq.addListener) mq.addListener(apply);
  }

  if (document.readyState !== 'loading') initFooterDisclosure();
  else document.addEventListener('DOMContentLoaded', initFooterDisclosure);
})();
